import { useEffect, useState, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigation } from '@/contexts/NavigationContext';
import { useTeams } from '@/hooks/useTeams';
import { useCreateAgent, useUpdateAgent, useDeleteAgent } from '@/hooks/useAgents';
import { useResourcePermissions } from '@/hooks/usePermissions';
import { useProfile } from '@/hooks/useProfile';
import { useModels } from '@/hooks/useModels';
import { useAgentFiles, useUploadAgentFile, useDeleteAgentFile } from '@/hooks/useAgentFiles';
import ModelSelectorModal from '@/components/modals/ModelSelectorModal';
import { toast } from 'sonner';
import { ArrowLeft, Upload, X, FileText, Image, Loader2, Trash2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useQuery } from '@tanstack/react-query';
import { ContextBudgetIndicator } from '@/components/ContextBudgetIndicator';
import { ResourceAccessSection } from '@/components/ResourceAccessSection';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import type { Tables } from '@/integrations/supabase/types';

type ScopeType = 'global' | 'team' | 'personal';

const agentSchema = z.object({
  name: z.string().trim().min(2, 'Nome deve ter pelo menos 2 caracteres').max(100),
  description: z.string().trim().max(500).optional().or(z.literal('')),
  model: z.string().min(1, 'Selecione um modelo'),
  system_prompt: z.string().trim().max(500000).optional().or(z.literal('')),
  status: z.enum(['active', 'beta']).default('active'),
  scopeValue: z.string().min(1, 'Selecione um ambiente'),
});

type AgentFormValues = z.infer<typeof agentSchema>;

function parseScopeValue(value: string): { scope_type: ScopeType; scope_id: string | null; project_id: string | null } {
  if (value === 'global') return { scope_type: 'global', scope_id: null, project_id: null };
  if (value === 'personal') return { scope_type: 'personal', scope_id: null, project_id: null };
  if (value.startsWith('team:')) return { scope_type: 'team', scope_id: value.slice(5), project_id: null };
  if (value.startsWith('project:')) return { scope_type: 'global', scope_id: null, project_id: value.slice(8) };
  return { scope_type: 'global', scope_id: null, project_id: null };
}

function buildScopeValue(item: { scope_type: string; scope_id?: string | null; project_id?: string | null }): string {
  if (item.project_id) return `project:${item.project_id}`;
  if (item.scope_type === 'team' && item.scope_id) return `team:${item.scope_id}`;
  if (item.scope_type === 'personal') return 'personal';
  return 'global';
}

import {
  ACCEPT_ALL,
  MAX_AGENT_FILES,
  isSupportedExtension,
  formatFileSize,
  validateUploadSize,
} from '@/lib/fileConstants';

export const AgentFormPage = () => {
  const { user } = useAuth();
  const { navigation, editingResource, setEditingResource } = useNavigation();
  const { data: teams } = useTeams();
  const { data: models } = useModels();
  const { isAdmin } = useProfile();
  const createAgent = useCreateAgent();
  const updateAgent = useUpdateAgent();
  const deleteAgent = useDeleteAgent();
  const [modelSelectorOpen, setModelSelectorOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [isDraggingFiles, setIsDraggingFiles] = useState(false);
  const dragCounterRef = useRef(0);

  const agentId = editingResource?.id;
  const isEditing = !!agentId;

  const { data: agent } = useQuery({
    queryKey: ['agent-detail', agentId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('agents')
        .select('*')
        .eq('id', agentId!)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!agentId,
  });
  const { canEdit: canManageAgentAccess } = useResourcePermissions(agent?.owner_id ?? '', agent?.project_id ?? undefined);

  const { data: agentFiles = [] } = useAgentFiles(agentId);
  const uploadFile = useUploadAgentFile();
  const deleteFile = useDeleteAgentFile();

  const defaultModelId = models?.[0]?.id ?? '';

  const defaultScopeValue = () => {
    if (navigation.projectId) return `project:${navigation.projectId}`;
    if (navigation.isPersonal) return 'personal';
    return 'global';
  };

  const form = useForm<AgentFormValues>({
    resolver: zodResolver(agentSchema),
    defaultValues: {
      name: '',
      description: '',
      model: defaultModelId,
      system_prompt: '',
      status: 'active',
      scopeValue: defaultScopeValue(),
    },
  });

  useEffect(() => {
    if (agent) {
      form.reset({
        name: agent.name,
        description: agent.description ?? '',
        model: agent.model,
        system_prompt: agent.system_prompt ?? '',
        status: agent.status as 'active' | 'beta',
        scopeValue: buildScopeValue(agent),
      });
    }
  }, [agent]);

  const onSubmit = async (values: AgentFormValues) => {
    const { scope_type, scope_id, project_id } = parseScopeValue(values.scopeValue);

    try {
      if (isEditing && agentId) {
        await updateAgent.mutateAsync({
          id: agentId,
          name: values.name,
          description: values.description || null,
          model: values.model,
          system_prompt: values.system_prompt || null,
          status: values.status,
          scope_type,
          scope_id,
          project_id,
        });
        toast.success('Agente atualizado');
      } else {
        const newAgent = await createAgent.mutateAsync({
          name: values.name,
          description: values.description || null,
          model: values.model,
          system_prompt: values.system_prompt || null,
          status: values.status,
          scope_type,
          scope_id,
          project_id,
        });

        // Upload pending files with the new agent ID
        if (pendingFiles.length > 0 && newAgent?.id) {
          for (const file of pendingFiles) {
            try {
              await uploadFile.mutateAsync({ agentId: newAgent.id, file });
            } catch {
              toast.error(`Erro ao enviar ${file.name}`);
            }
          }
        }

        toast.success('Agente criado com sucesso');
      }
      setEditingResource(null);
    } catch (err) {
      toast.error(`Erro ao salvar agente: ${(err as Error).message}`);
    }
  };

  const handleFileUpload = async (files: FileList | null) => {
    if (!files) return;

    if (isEditing && agentId) {
      // Editing mode: upload immediately
      const currentCount = agentFiles.length;
      for (let i = 0; i < files.length; i++) {
        if (currentCount + i >= MAX_AGENT_FILES) {
          toast.error(`Máximo de ${MAX_AGENT_FILES} arquivos por agente`);
          break;
        }
        const file = files[i];
        if (!isSupportedExtension(file.name)) {
          const ext = file.name.split('.').pop()?.toLowerCase() || '';
          toast.error(`Tipo não suportado: .${ext}`);
          continue;
        }
        const sizeError = validateUploadSize(file);
        if (sizeError) {
          toast.error(`${file.name}: ${sizeError}`);
          continue;
        }
        try {
          await uploadFile.mutateAsync({ agentId, file });
          toast.success(`${file.name} enviado`);
        } catch {
          toast.error(`Erro ao enviar ${file.name}`);
        }
      }
    } else {
      // Creation mode: queue locally
      const currentCount = pendingFiles.length;
      const newFiles: File[] = [];
      for (let i = 0; i < files.length; i++) {
        if (currentCount + newFiles.length >= MAX_AGENT_FILES) {
          toast.error(`Máximo de ${MAX_AGENT_FILES} arquivos por agente`);
          break;
        }
        const file = files[i];
        if (!isSupportedExtension(file.name)) {
          const ext = file.name.split('.').pop()?.toLowerCase() || '';
          toast.error(`Tipo não suportado: .${ext}`);
          continue;
        }
        const sizeError = validateUploadSize(file);
        if (sizeError) {
          toast.error(`${file.name}: ${sizeError}`);
          continue;
        }
        newFiles.push(file);
      }
      if (newFiles.length > 0) {
        setPendingFiles(prev => [...prev, ...newFiles]);
      }
    }
    // Reset input
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDeleteFile = async (file: { id: string; storage_path: string }) => {
    if (!agentId) return;
    try {
      await deleteFile.mutateAsync({ fileId: file.id, storagePath: file.storage_path, agentId });
      toast.success('Arquivo removido');
    } catch {
      toast.error('Erro ao remover arquivo');
    }
  };

  const handleDeleteAgent = async () => {
    if (!agentId) return;
    try {
      await deleteAgent.mutateAsync(agentId);
      toast.success('Agente excluído');
      setDeleteOpen(false);
      setEditingResource(null);
    } catch (err) {
      toast.error(`Erro ao excluir agente: ${(err as Error).message}`);
    }
  };

  const isPending = createAgent.isPending || updateAgent.isPending || deleteAgent.isPending;
  const selectedModelDisplay = models?.find(m => m.id === form.watch('model'))?.display_name ?? form.watch('model') ?? 'Selecionar modelo';

  return (
    <div className="flex-1 flex flex-col overflow-hidden min-h-0">
      {/* Header */}
      <div className="flex items-center gap-3 px-6 py-4 border-b border-color">
        <button
          onClick={() => setEditingResource(null)}
          className="p-1.5 rounded-md text-tertiary hover:text-primary hover:bg-hover transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <h1 className="text-lg font-semibold text-primary">
          {isEditing ? 'Editar Agente' : 'Novo Agente'}
        </h1>
      </div>

      {/* Form */}
      <div className="flex-1 overflow-y-auto p-6">
        <form onSubmit={form.handleSubmit(onSubmit)} className="max-w-4xl mx-auto space-y-6">
          {/* Nome + Status */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-primary mb-1.5">Nome *</label>
              <input
                {...form.register('name')}
                className="w-full px-3 py-2 rounded-md border border-color bg-surface text-sm text-primary focus:outline-none focus:ring-2 focus:ring-brand-primary"
                placeholder="Nome do agente"
              />
              {form.formState.errors.name && (
                <p className="text-xs text-destructive mt-1">{form.formState.errors.name.message}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-primary mb-1.5">Status</label>
              <select
                {...form.register('status')}
                className="w-full px-3 py-2 rounded-md border border-color bg-surface text-sm text-primary focus:outline-none focus:ring-2 focus:ring-brand-primary"
              >
                <option value="active">Ativo</option>
                <option value="beta">Beta</option>
              </select>
            </div>
          </div>

          {/* Descrição */}
          <div>
            <label className="block text-sm font-medium text-primary mb-1.5">Descrição</label>
            <textarea
              {...form.register('description')}
              rows={2}
              className="w-full px-3 py-2 rounded-md border border-color bg-surface text-sm text-primary focus:outline-none focus:ring-2 focus:ring-brand-primary resize-none"
              placeholder="Descrição opcional"
            />
          </div>

          {/* Modelo + Ambiente */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-primary mb-1.5">Modelo *</label>
              <button
                type="button"
                onClick={() => setModelSelectorOpen(true)}
                className="w-full px-3 py-2 rounded-md border border-color bg-surface text-sm text-primary text-left focus:outline-none focus:ring-2 focus:ring-brand-primary hover:bg-hover"
              >
                {selectedModelDisplay}
              </button>
            </div>
            <div>
              <label className="block text-sm font-medium text-primary mb-1.5">Ambiente *</label>
              <select
                {...form.register('scopeValue')}
                className="w-full px-3 py-2 rounded-md border border-color bg-surface text-sm text-primary focus:outline-none focus:ring-2 focus:ring-brand-primary"
              >
                <option value="global">🏢 Espaço Caldeira</option>
                {teams?.map(team => (
                  <option key={team.id} value={`team:${team.id}`}>{team.icon} Time — {team.name}</option>
                ))}
                <option value="personal">👤 Pessoal</option>
                {navigation.projectId && (
                  <option value={`project:${navigation.projectId}`}>📁 Projeto atual</option>
                )}
              </select>
            </div>
          </div>

          {isEditing && agent && (
            <ResourceAccessSection
              type="agent"
              resourceId={agent.id}
              ownerId={agent.owner_id}
              scopeType={agent.scope_type}
              projectId={agent.project_id}
              canManage={isAdmin || canManageAgentAccess}
            />
          )}

          {/* System Prompt */}
          <div>
            <label className="block text-sm font-medium text-primary mb-1.5">Prompt do sistema</label>
            <textarea
              {...form.register('system_prompt')}
              rows={16}
              className="w-full px-3 py-2 rounded-md border border-color bg-surface text-sm text-primary focus:outline-none focus:ring-2 focus:ring-brand-primary resize-y font-mono text-xs leading-relaxed"
              placeholder="Instruções para o agente..."
            />
            <ContextBudgetIndicator
              value={form.watch('system_prompt') ?? ''}
              modelId={form.watch('model')}
            />
          </div>

          {/* Files section */}
          <div
            className={`rounded-lg transition-colors -mx-2 px-2 py-2 ${
              isDraggingFiles
                ? "ring-2 ring-brand-primary ring-offset-2 ring-offset-background bg-brand-primary/5"
                : ""
            }`}
            onDragEnter={(e) => {
              if (!Array.from(e.dataTransfer.types).includes("Files")) return;
              e.preventDefault();
              dragCounterRef.current += 1;
              if (dragCounterRef.current === 1) setIsDraggingFiles(true);
            }}
            onDragOver={(e) => {
              if (!Array.from(e.dataTransfer.types).includes("Files")) return;
              e.preventDefault();
            }}
            onDragLeave={(e) => {
              if (!Array.from(e.dataTransfer.types).includes("Files")) return;
              dragCounterRef.current = Math.max(0, dragCounterRef.current - 1);
              if (dragCounterRef.current === 0) setIsDraggingFiles(false);
            }}
            onDrop={(e) => {
              if (!Array.from(e.dataTransfer.types).includes("Files")) return;
              e.preventDefault();
              dragCounterRef.current = 0;
              setIsDraggingFiles(false);
              handleFileUpload(e.dataTransfer.files);
            }}
          >
            <label className="block text-sm font-medium text-primary mb-1.5">
              Arquivos do Agente
              <span className="text-tertiary font-normal ml-2">
                ({isEditing ? agentFiles.length : pendingFiles.length}/{MAX_AGENT_FILES})
              </span>
            </label>
            <p className="text-xs text-tertiary mb-3">
              Arquivos anexados serão enviados como contexto em cada mensagem das conversas com este agente. Você pode arrastar arquivos aqui ou clicar pra adicionar.
            </p>

            {/* Existing files (editing mode) */}
            {isEditing && agentFiles.length > 0 && (
              <div className="space-y-2 mb-3">
                {agentFiles.map(file => (
                  <div key={file.id} className="flex items-center gap-3 px-3 py-2 rounded-md border border-color bg-surface">
                    {file.file_type === 'image' ? (
                      <Image className="w-4 h-4 text-tertiary shrink-0" />
                    ) : (
                      <FileText className="w-4 h-4 text-tertiary shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-primary truncate">{file.file_name}</p>
                      <p className="text-xs text-tertiary">
                        {formatFileSize(file.file_size_bytes)}
                        {file.parsing_status === 'pending' && ' · Processando...'}
                        {file.parsing_status === 'error' && ' · Erro no processamento'}
                        {file.parsing_status === 'ready' && ' · Pronto'}
                      </p>
                    </div>
                    {file.parsing_status === 'pending' && (
                      <Loader2 className="w-4 h-4 text-tertiary animate-spin shrink-0" />
                    )}
                    <button
                      type="button"
                      onClick={() => handleDeleteFile(file)}
                      className="p-1 rounded-md text-tertiary hover:text-destructive hover:bg-hover transition-colors shrink-0"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Pending files (creation mode) */}
            {!isEditing && pendingFiles.length > 0 && (
              <div className="space-y-2 mb-3">
                {pendingFiles.map((file, idx) => (
                  <div key={`${file.name}-${idx}`} className="flex items-center gap-3 px-3 py-2 rounded-md border border-color bg-surface">
                    {file.type.startsWith('image/') ? (
                      <Image className="w-4 h-4 text-tertiary shrink-0" />
                    ) : (
                      <FileText className="w-4 h-4 text-tertiary shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-primary truncate">{file.name}</p>
                      <p className="text-xs text-tertiary">{formatFileSize(file.size)}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setPendingFiles(prev => prev.filter((_, i) => i !== idx))}
                      className="p-1 rounded-md text-tertiary hover:text-destructive hover:bg-hover transition-colors shrink-0"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Upload button */}
            {(isEditing ? agentFiles.length : pendingFiles.length) < MAX_AGENT_FILES && (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={isEditing && uploadFile.isPending}
                className="flex items-center gap-2 px-4 py-2 rounded-md border border-dashed border-color text-sm text-tertiary hover:text-primary hover:border-brand-primary hover:bg-hover transition-colors disabled:opacity-50"
              >
                {isEditing && uploadFile.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Upload className="w-4 h-4" />
                )}
                Adicionar arquivo
              </button>
            )}

            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPT_ALL}
              multiple
              className="hidden"
              onChange={e => handleFileUpload(e.target.files)}
            />
          </div>

          {/* Actions */}
          <div className="flex items-center gap-3 pb-6">
            <button
              type="submit"
              disabled={isPending}
              className="px-6 py-2.5 brand-primary rounded-md text-sm font-medium disabled:opacity-50"
            >
              {isPending ? 'Salvando...' : isEditing ? 'Salvar Alterações' : 'Criar Agente'}
            </button>
            <button
              type="button"
              onClick={() => setEditingResource(null)}
              className="px-6 py-2.5 border border-color rounded-md text-sm font-medium text-primary hover:bg-hover"
            >
              Cancelar
            </button>
            {isEditing && isAdmin && (
              <button
                type="button"
                onClick={() => setDeleteOpen(true)}
                disabled={isPending}
                className="ml-auto px-4 py-2.5 border border-destructive text-destructive rounded-md text-sm font-medium hover:bg-destructive/10 disabled:opacity-50 flex items-center gap-2"
              >
                <Trash2 className="w-4 h-4" />
                Excluir agente
              </button>
            )}
          </div>
        </form>
      </div>

      <ModelSelectorModal
        open={modelSelectorOpen}
        onOpenChange={setModelSelectorOpen}
        value={form.watch('model')}
        onSelect={(modelId) => {
          form.setValue('model', modelId);
          setModelSelectorOpen(false);
        }}
      />

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir agente?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação não pode ser desfeita. O agente será removido da biblioteca e não ficará mais disponível para uso.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteAgent.isPending}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteAgent}
              disabled={deleteAgent.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteAgent.isPending ? 'Excluindo...' : 'Excluir agente'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
